import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import MuiList from "@mui/material/List";
import ConnectorMenuItem from "layouts/Flow Designer/components/ConnectorMenuItem";
import ToolboxDetailHeader from "../ToolboxDetailHeader";
import ToolboxDetailSubHeader from "../ToolboxDetailSubHeader";
import ToolboxDetailRegisterConnector from "../ToolboxDetailRegisterConnector";
import {
  startDragComponent,
  stopDragComponent
} from "redux/actions/flowDesigner/flowsActionCreator";
import ProtectedButton from "authentication/ProtectedButton";
import { PERMISSIONS } from "constants/portal";
import useConnectorShared from "hooks/useConnectorShared";
import { isConnectorInFlow } from "layouts/Flow Designer/helpers/common";
import { useDetermineShareFlowUserAccessType } from "hooks/useDetermineShareFlowUserAccessType";
import { usePermissions } from "hooks/usePermissions";
import useAllowEditWorkflow from "hooks/useAllowEditWorkflow";

const List = styled(MuiList)(({ theme }) => ({
  overflow: "hidden",
  overflowY: "auto",
  maxHeight: "500px",
  minHeight: "141px",

  scrollbarWidth: "thin", // Firefox
  scrollbarColor: `${theme.palette.grey[400]} transparent`,

  "&::-webkit-scrollbar": {
    width: "8px",
    background: "transparent",
    transition: "opacity 0.2s",
    opacity: 0
  },
  "&:hover::-webkit-scrollbar": {
    opacity: 1
  },
  "&::-webkit-scrollbar-thumb": {
    background: theme.palette.grey[400],
    borderRadius: "8px"
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent"
  }
}));

const ConnectorsList = () => {
  const { isConnectorOwnedShared } = useConnectorShared();
  const { components } = useSelector((store) => store?.designer?.cxfComponents);
  const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
  const dispatch = useDispatch();
  const { t } = useTranslation("flowDesigner");
  const { isReadOnlyAccess } = useDetermineShareFlowUserAccessType(currentSavedFlow?.id);
  const { canViewAnyConnectors } = usePermissions();
  const { allowEditWorkflow } = useAllowEditWorkflow();

  const onDragStart = (event, nodeType, nodeDataset) => {
    var nodeData = {
      nodeType: nodeType,
      nodeData: nodeDataset
    };
    event.dataTransfer.setData("application/reactflow", JSON.stringify(nodeData));
    event.dataTransfer.effectAllowed = "move";
    dispatch(startDragComponent());
  };

  const onDragEnd = (event) => {
    event.preventDefault();
    dispatch(stopDragComponent());
  };

  return (
    <Box
      py={2}
      pl={2}
      pr={2}
      sx={{ flexGrow: 1, height: "100%", display: "flex", flexDirection: "column" }}
    >
      <ToolboxDetailHeader title={t("toolbox.headers.connectors")} />
      <ProtectedButton permissionName={PERMISSIONS.CREATE_CONNECTORS}>
        <ToolboxDetailRegisterConnector />
      </ProtectedButton>
      <ToolboxDetailSubHeader
        title={t("toolbox.titles.library")}
        subtitle={t("toolbox.subtitles.library_connectors")}
      />
      <List
        sx={{
          width: 295,
          height: "auto",
          maxHeight: 500,
          minHeight: 141,
          overflowY: "auto",
          overflowX: "hidden"
        }}
      >
        {[...(components || [])]
          .sort((firstConnector, secondConnector) => (firstConnector.label || firstConnector.name || "").localeCompare(secondConnector.label || secondConnector.name || "", undefined, { sensitivity: "base" }))
          .filter(
            (connector) =>
              (connector.status === "active" && isConnectorInFlow(connector, currentSavedFlow)) ||
              isConnectorOwnedShared(connector.id) ||
              canViewAnyConnectors()
          )
          .map((comp) => (
            <ConnectorMenuItem
              key={comp.id}
              component={comp}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              allowDescription
              allowDragAndDrop
              allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)}
            />
          ))}
      </List>
    </Box>
  );
};

export default ConnectorsList;
